import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import styled from "@emotion/styled";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RecoilRoot } from "recoil";
import SignUp from "@containers/SignUp";
import EmailAddress from "@containers/SignUp/EmailAddress";
import CreateAccount from "@containers/SignUp/CreateAccount";
import Login from "@containers/Login";
import StyleLibrary from "@containers/StyleLibrary";
import MyLibrary from "@containers/MyLibrary";
import ColorPage from "@containers/Color";
import SubscriptionPage from "@containers/Subscription";
import AdminDashboard from "@containers/AdminDashboard";
import PresetsAdminDashboard from "@containers/PresetsAdminDashboard";
import Header, { HEADER_HEIGHT } from "@components/Header";
import { Footer, FOOTER_HEIGHT } from "@components/Footer";
import PrivateRoute from "@components/PrivateRoute";
import AdminRoute from "@components/AdminRoute";
import WandTechRoute from "@components/WandTechRoute";
import { AuthProvider } from "@contexts/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneNumber from "@containers/SignUp/PhoneNumber";
import VerifyOtp from "@containers/SignUp/VerifyOtp";
import { useAuth } from "@contexts/AuthContext";
import { Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { initializeAnalytics } from "@utils/analytics";
import { useEffect } from "react";

const queryClient = new QueryClient();

const LoginRedirect = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/color" />;
  }
  return <Login />; // Use the imported Login component here
};

const App: React.FC = () => {
  console.log("App component rendering");
  console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  useEffect(() => {
    initializeAnalytics();
  }, []);

  // useEffect(() => {
  //   // This sets the background color of the body
  //   document.body.style.backgroundColor = "#262626"; //
  //   // Optional: Reset the background color when the component unmounts
  //   return () => {
  //     document.body.style.backgroundColor = ""; // Resets to default background color
  //   };
  // }, []);

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <RecoilRoot>
        <ToastContainer />
        <Router>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Header />
              <PageWrapper>
                <Routes>
                  <Route path="/" element={<LoginRedirect />} />
                  <Route path="/sign-up" element={<SignUp />} />
                  <Route
                    path="/sign-up/email-address"
                    element={<EmailAddress />}
                  />
                  <Route path="/sign-up/phone-number" element={<PhoneNumber />} />
                  <Route path="/sign-up/verify-otp" element={<VerifyOtp />} />
                  <Route
                    path="/sign-up/create-account"
                    element={<CreateAccount />}
                  />
                  <Route path="/login" element={<LoginRedirect />} />
                  <Route element={<PrivateRoute />}>
                    <Route path="/style-library" element={<StyleLibrary />} />
                    <Route path="/my-library" element={<MyLibrary />} />
                    <Route path="/color" element={<ColorPage />} />
                    <Route path="/subscription" element={<SubscriptionPage />} />
                  </Route>
                  <Route element={<AdminRoute />}>
                    <Route path="/admin" element={<AdminDashboard />} />
                  </Route>
                  <Route element={<WandTechRoute />}>
                    <Route path="/presets-admin" element={<PresetsAdminDashboard />} />
                  </Route>
                </Routes>
              </PageWrapper>
              <Footer />
            </AuthProvider>
          </QueryClientProvider>
        </Router>
      </RecoilRoot>
    </Sentry.ErrorBoundary>
  );
};

// Error fallback component
const ErrorFallback = ({ error, resetError }: any) => (
  <div style={{ padding: "20px", textAlign: "center" }}>
    <h2>Something went wrong</h2>
    <p>We've been notified about this error and will fix it soon.</p>
    <button onClick={resetError}>Try again</button>
  </div>
);

const PageWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - ${HEADER_HEIGHT}px - ${FOOTER_HEIGHT}px);
`;

export default App;
